import { useEffect, useState } from "react"
import DraggableList from "components/Common/DraggableList"
import {
  Row,
  Col,
  Label,
  Button,
  Badge,
  Card,
  CardBody,
  Input,
  Spinner,
} from "reactstrap"
import { useDynamicForm } from "hooks/useDynamicForm"
import { useVehicleSearchQuery } from "hooks/stockHook"
import VehicleShortInfoCard from "./VehicleShortInfoCard"
import { useMediaQuery } from "react-responsive"

const VehicleSpecification = ({
  // Only receive props that are truly needed from parent
  onVehicleSpecsChange,
  onMultiCarSpecsChange,
  onMultiCarModeChange,
  initialMultiCarMode = false,
  initialVehicleSpecs = [],
  initialMultiCarSpecs = [],
  // Optional: Pass custom search API function
  customSearchAPI,
  selectedVehicle,
  setSelectedVehicle,
  existVehicleLoading,
  existVehicleData
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 })

  // Internal state management
  const [multiCarMode, setMultiCarMode] = useState(initialMultiCarMode)
  const [showVehicleSuggestions, setShowVehicleSuggestions] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  // Vehicle search hook
  const {
    data: searchResults,
    isLoading: searchLoading,
    error: searchError,
  } = useVehicleSearchQuery(searchTerm, {
    limit: 3,
    enabled: searchTerm?.length >= 2,
  })

  // Custom hooks for dynamic forms
  const {
    items: vehicleSpecs,
    addItem: addVehicleSpec,
    removeItem: removeVehicleSpec,
    updateItem: updateVehicleSpec,
    handleDragEnd: handleSpecsDragEnd,
    resetItems: resetVehicleSpecs,
  } = useDynamicForm(
    initialVehicleSpecs.length > 0
      ? initialVehicleSpecs
      : [
          { id: "1", key: "Vehicle Name", value: "", type: "text" },
          { id: "2", key: "Chassis No", value: "", type: "text" },
          { id: "3", key: "Engine No", value: "", type: "text" },
          { id: "4", key: "Model", value: "", type: "text" },
          { id: "5", key: "Color", value: "", type: "text" },
          { id: "6", key: "CC", value: "", type: "text" },
          { id: "7", key: "Fuel Type", value: "", type: "text" },
          { id: "8", key: "Descriptions", value: "", type: "textarea" },
        ]
  )

  const {
    items: multiCarSpecs,
    addItem: addMultiCarSpec,
    removeItem: removeMultiCarSpec,
    updateItem: updateMultiCarSpec,
    resetItems: resetMultiCarSpecs,
  } = useDynamicForm(
    initialMultiCarSpecs.length > 0
      ? initialMultiCarSpecs
      : [
          {
            id: "1",
            vehicleName: "",
            chassisNo: "",
            model: "",
            color: "",
            unitPrice: "",
          },
        ]
  )

  // Handle multi-car mode toggle
  const handleMultiCarModeToggle = newMode => {
    setMultiCarMode(newMode)
    setShowVehicleSuggestions(false)
    setSearchTerm("")
    if (onMultiCarModeChange) {
      onMultiCarModeChange(newMode)
    }
  }

  // Handle vehicle selection from suggestions
  const handleVehicleSelect = vehicle => {
    setSelectedVehicle(vehicle)
    const updatedSpecs = vehicleSpecs?.map(spec => {
      vehicle.full_name = `${vehicle?.brand?.name ?? ""} ${
        vehicle?.model?.name ?? ""
      } ${vehicle?.package?.name ?? ""} ${
        vehicle?.engine_type?.name ?? ""
      }`.trim()

      switch (spec.key) {
        case "Vehicle Name":
          return { ...spec, value: vehicle.full_name ?? "" }
        case "Chassis No":
          return {
            ...spec,
            value: vehicle?.full_chassis ?? vehicle?.chassis_code?.name ?? "",
          }
        case "Engine No":
          return {
            ...spec,
            value: vehicle?.full_engine ?? vehicle?.engine_code?.name ?? "",
          }
        case "Model":
          return { ...spec, value: vehicle?.year?.name ?? "" }
        case "Color":
          return { ...spec, value: vehicle?.exterior_color?.name ?? "" }
        case "CC":
          return { ...spec, value: vehicle?.engine_c_c?.name ?? "" }
        case "Fuel Type":
          return { ...spec, value: vehicle?.fuel_type?.name ?? "" }
        case "Descriptions":
          return { ...spec, value: vehicle?.short_descriptions ?? "" }
        default:
          return spec
      }
    })
    resetVehicleSpecs(updatedSpecs)
    setShowVehicleSuggestions(false)
    setSearchTerm("")

    // Notify parent of the changes
    if (onVehicleSpecsChange) {
      onVehicleSpecsChange(updatedSpecs)
    }
  }

  // Handle manual search trigger
  const handleManualSearch = () => {
    if (searchTerm?.length >= 2) {
      if (customSearchAPI) {
        customSearchAPI(searchTerm)
      } else {
        searchVehicles(searchTerm)
      }
    }
  }

  // Close suggestions
  const handleCloseSuggestions = () => {
    setShowVehicleSuggestions(false)
    setSearchTerm("")
  }

  // Enhanced update functions that notify parent
  const handleUpdateVehicleSpec = (id, field, value) => {
    updateVehicleSpec(id, field, value)
    if (onVehicleSpecsChange) {
      const updatedSpecs = vehicleSpecs?.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
      onVehicleSpecsChange(updatedSpecs)
    }
  }

  const handleUpdateMultiCarSpec = (id, field, value) => {
    updateMultiCarSpec(id, field, value)
    if (onMultiCarSpecsChange) {
      const updatedSpecs = multiCarSpecs?.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
      onMultiCarSpecsChange(updatedSpecs)
    }
  }

  const handleAddMultiCarSpec = newSpec => {
    addMultiCarSpec(newSpec)
    if (onMultiCarSpecsChange) {
      onMultiCarSpecsChange([...multiCarSpecs, newSpec])
    }
  }

  const handleRemoveMultiCarSpec = id => {
    removeMultiCarSpec(id)
    if (onMultiCarSpecsChange) {
      onMultiCarSpecsChange(multiCarSpecs.filter(item => item.id !== id))
    }
  }

  const handleAddVehicleSpec = newSpec => {
    addVehicleSpec(newSpec)
    if (onVehicleSpecsChange) {
      onVehicleSpecsChange([...vehicleSpecs, newSpec])
    }
  }

  const handleRemoveVehicleSpec = id => {
    removeVehicleSpec(id)
    if (onVehicleSpecsChange) {
      onVehicleSpecsChange(vehicleSpecs.filter(item => item.id !== id))
    }
  }

  // set existing vehicle specs when selectedVehicle changes
  useEffect(() => {
    if (existVehicleData) {
      handleVehicleSelect(existVehicleData)
    }
  }, [existVehicleData])


  // Render vehicle specification item
  const renderVehicleSpec = (spec, index) => (
    <Row className="g-2 mb-2">
      <Col xs={12} sm={5} md={5} lg={5} xl={5}>
        <Input
          type="text"
          value={spec.key}
          onChange={e =>
            handleUpdateVehicleSpec(spec.id, "key", e.target.value)
          }
          placeholder="Specification name"
          className="form-control-sm"
          size={isMobile ? "sm" : undefined}
        />
      </Col>
      <Col xs={12} sm={7} md={7} lg={7} xl={7}>
        <Input
          type={spec?.type === "textarea" ? "textarea" : "text"}
          value={spec.value}
          onChange={e =>
            handleUpdateVehicleSpec(spec.id, "value", e.target.value)
          }
          placeholder="Enter value"
          className="form-control-sm"
          rows={spec?.type === "textarea" ? (isMobile ? 2 : 3) : 1}
          size={isMobile ? "sm" : undefined}
        />
      </Col>
    </Row>
  )

  // Render multi-car specification item
  const renderMultiCarSpec = (car, index) => (
    <Card className="mb-3 border">
      <CardBody className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">
            <i className="bx bx-car me-2"></i>
            Vehicle {index + 1}
          </h6>
          {multiCarSpecs.length > 1 && (
            <Button
              color="outline-danger"
              size="sm"
              onClick={() => handleRemoveMultiCarSpec(car.id)}
            >
              <i className="bx bx-trash"></i>
            </Button>
          )}
        </div>
        <Row className="g-2">
          <Col md={6}>
            <Input
              type="text"
              value={car.vehicleName}
              onChange={e =>
                handleUpdateMultiCarSpec(car.id, "vehicleName", e.target.value)
              }
              placeholder="Vehicle Name"
              className="form-control-sm"
            />
          </Col>
          <Col md={6}>
            <Input
              type="text"
              value={car.chassisNo}
              onChange={e =>
                handleUpdateMultiCarSpec(car.id, "chassisNo", e.target.value)
              }
              placeholder="Chassis No"
              className="form-control-sm"
            />
          </Col>
          <Col md={4}>
            <Input
              type="text"
              value={car.model}
              onChange={e =>
                handleUpdateMultiCarSpec(car.id, "model", e.target.value)
              }
              placeholder="Model"
              className="form-control-sm"
            />
          </Col>
          <Col md={4}>
            <Input
              type="text"
              value={car.color}
              onChange={e =>
                handleUpdateMultiCarSpec(car.id, "color", e.target.value)
              }
              placeholder="Color"
              className="form-control-sm"
            />
          </Col>
          <Col md={4}>
            <Input
              type="number"
              value={car.unitPrice}
              onChange={e =>
                handleUpdateMultiCarSpec(car.id, "unitPrice", e.target.value)
              }
              placeholder="Unit Price"
              min="0"
              step="0.01"
              className="form-control-sm"
            />
          </Col>
        </Row>
      </CardBody>
    </Card>
  )

  return (
    <Row className="mb-4">
      <Col xs={12}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Label className="fw-bold">Vehicle Specifications:</Label>
          <div className="d-flex gap-2">
            <Badge
              color={multiCarMode ? "primary" : "secondary"}
              className="cursor-pointer"
              onClick={() => handleMultiCarModeToggle(!multiCarMode)}
            >
              <i
                className={`bx ${multiCarMode ? "bx-carousel" : "bx-car"} me-1`}
              ></i>
              {multiCarMode ? "Multi-Car Mode" : "Single Car Mode"}
            </Badge>

            {selectedVehicle && (
              <Button
                color="outline-danger"
                size="sm"
                onClick={() => {
                  setSelectedVehicle(null)
                }}
                type="button"
              >
                <i className="bx bx-x me-1"></i>
                Clear
              </Button>
            )}

            {!multiCarMode && (
              <Button
                color="outline-primary"
                size="sm"
                onClick={() => {
                  setShowVehicleSuggestions(!showVehicleSuggestions)
                  if (!showVehicleSuggestions) {
                    setSearchTerm("")
                  }
                }}
                type="button"
              >
                <i className="bx bx-search me-1"></i>
                Search Vehicle
              </Button>
            )}
          </div>
        </div>

        {selectedVehicle?.stock_id && (
          <div className="quotation-process-card text-center p-4 mb-3 rounded-3 shadow-sm border position-relative">
            <div className="d-flex justify-content-center align-items-center mb-3">
              <i
                className="bx bx-car text-primary"
                style={{ fontSize: "2rem" }}
              ></i>
            </div>

            <h5 className="fw-bold text-dark mb-2">
              You have selected this vehicle for quotation
            </h5>

            <p className="text-muted mb-3">
              Stock ID:{" "}
              <span className="fw-semibold text-primary">
                {selectedVehicle?.stock_id ?? ""}
              </span>
              , Chassis:{" "}
              <span className="fw-semibold text-primary">
                {selectedVehicle?.full_chassis ?? ""}
              </span>
            </p>

            <div className="small text-secondary fst-italic">
              Our system is now preparing the quotation based on your selection.
              <br /> Please review the details before submitting.
            </div>

            <div className="position-absolute top-0 start-0 w-100 h-100 quote-overlay"></div>
          </div>
        )}

        {showVehicleSuggestions && !multiCarMode && (
          <Card className="mb-3">
            <CardBody className="p-2">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">
                  Search Vehicle by Name, Chassis, or Engine No:
                </small>
                <Button
                  color="link"
                  size="sm"
                  className="p-0"
                  onClick={handleCloseSuggestions}
                >
                  <i className="bx bx-x"></i>
                </Button>
              </div>

              {/* Search Input */}
              <div className="mb-2">
                <Input
                  type="text"
                  placeholder="Type vehicle name, chassis no, stock id, or engine no..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="form-control-sm"
                />
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <small className="text-muted">
                    {searchTerm?.length === 0 && searchTerm?.length < 2
                      ? "Type at least 2 characters..."
                      : searchLoading || existVehicleLoading
                      ? ""
                      : `${searchResults?.length ?? 0} vehicles found`}
                  </small>

                  {searchTerm?.length >= 2 && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleManualSearch}
                      disabled={searchLoading || existVehicleLoading}
                      className="ms-2"
                    >
                      {searchLoading || existVehicleLoading ? (
                        <Spinner size="sm" />
                      ) : (
                        "Search"
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Loading State */}
              {(searchLoading || existVehicleLoading) && (
                <div className="text-center p-3">
                  <Spinner size="sm" className="me-2" />
                  <small>Searching vehicles...</small>
                </div>
              )}

              {/* Error State */}
              {searchError && (
                <div className="text-center p-2 text-danger">
                  <small>{searchError}</small>
                </div>
              )}

              {/* Search Results */}
              {!searchLoading && !existVehicleLoading && searchResults?.length > 0 && (
                <div className="search-results">
                  {searchResults?.map(vehicle => (
                    <div
                      key={vehicle.id}
                      className="p-2 border-bottom cursor-pointer hover-bg"
                      onClick={() => handleVehicleSelect(vehicle)}
                    >
                      <VehicleShortInfoCard vehicle={vehicle} />
                    </div>
                  ))}
                </div>
              )}

              {/* No Results */}
              {!searchLoading &&
                !existVehicleLoading &&
                searchTerm?.length >= 2 &&
                searchResults.length === 0 && (
                  <div className="text-center p-2 text-muted">
                    <small>
                      No vehicles found. Try different search terms.
                    </small>
                  </div>
                )}

              {/* Initial State */}
              {!searchLoading &&
                !existVehicleLoading &&
                searchTerm?.length === 0 &&
                searchResults?.length === 0 && (
                  <div className="text-center p-2 text-muted">
                    <small>Start typing to search for vehicles...</small>
                  </div>
                )}
            </CardBody>
          </Card>
        )}

        {multiCarMode ? (
          <div>
            {multiCarSpecs?.map((car, index) => renderMultiCarSpec(car, index))}
            <Button
              color="outline-primary"
              size="sm"
              onClick={() =>
                handleAddMultiCarSpec({
                  vehicleName: "",
                  chassisNo: "",
                  model: "",
                  color: "",
                  unitPrice: 0,
                })
              }
              className="w-100"
            >
              <i className="bx bx-plus me-1"></i>
              Add Another Vehicle
            </Button>
          </div>
        ) : (
          <Card className="border">
            <CardBody className="p-3">
              <DraggableList
                items={vehicleSpecs}
                onDragEnd={handleSpecsDragEnd}
                onAdd={() => handleAddVehicleSpec({ key: "", value: "" })}
                onRemove={handleRemoveVehicleSpec}
                addButtonText="Add Specification"
                renderItem={renderVehicleSpec}
                minItems={1}
                compact
              />
            </CardBody>
          </Card>
        )}
      </Col>
    </Row>
  )
}

export default VehicleSpecification
